import { useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAccessApi } from '../services/api';
import { useUserPermissions } from '../hooks/useUserPermissions';
import { useUserRoles } from '../hooks/useUserRoles';
import {
    useOrganizationTeams,
    useAddUserToTeamOrgAdmin,
    useRemoveUserFromTeamOrgAdmin,
    useUpdateUserTeamRoleOrgAdmin,
} from '../hooks/useApi';
import { ROLES } from '@wevo/wevo-access';
import { LoadingPage } from '../components/LoadingSpinner';
import { RoleBadge } from '../components/RoleBadge';
import { useState } from 'react';

/** Shapes for team_memberships on org-admin user detail (not yet on ApiClient return type) */
type OrgAdminTeamMembership = {
    team_role_id: string | number;
    Team: { id: string; name: string };
    TeamRole?: { name: string } | null;
};

type OrgAdminUserWithTeamMemberships = {
    team_memberships?: OrgAdminTeamMembership[];
};

export function AdminUserDetailPage() {
    const { userId, orgId } = useParams<{ userId: string; orgId: string }>();
    const { hasPermission, isLoading: permissionsLoading } = useUserPermissions();
    const { hasRole } = useUserRoles();
    const { getOrganizationUserById, toggleUserAdminRole } = useAccessApi();
    const queryClient = useQueryClient();
    const [isTogglingAdmin, setIsTogglingAdmin] = useState(false);

    // Team management state
    const [showAddToTeamModal, setShowAddToTeamModal] = useState(false);
    const [showRemoveFromTeamModal, setShowRemoveFromTeamModal] = useState<{
        show: boolean;
        teamId?: string;
        teamName?: string;
    }>({ show: false });
    // Check if current user is a developer
    const isDeveloper = hasRole(ROLES.DEVELOPER);

    // Query for detailed user data from Auth0 via organization-admin endpoint
    const {
        data: userData,
        isLoading: userLoading,
        error: userError,
    } = useQuery({
        queryKey: ['adminUserDetail', orgId, userId],
        queryFn: async () => {
            if (!userId || !orgId) throw new Error('User ID and Organization ID are required');
            return getOrganizationUserById(orgId, userId);
        },
        enabled: !!userId && !!orgId && !permissionsLoading && hasPermission('administrate:users'),
        staleTime: 5 * 60 * 1000, // 5 minutes
        retry: 1,
        gcTime: 5 * 60 * 1000, // 5 minutes
        refetchOnWindowFocus: false,
    });

    // Query for available teams (for adding user to teams)
    const { data: orgTeamsData } = useOrganizationTeams(orgId || null);

    // Team management mutations
    const addUserToTeam = useAddUserToTeamOrgAdmin();
    const removeUserFromTeam = useRemoveUserFromTeamOrgAdmin();
    const updateUserTeamRole = useUpdateUserTeamRoleOrgAdmin();

    // Use team_memberships from the updated API response
    const userWithTeamMemberships = userData?.user as OrgAdminUserWithTeamMemberships | undefined;
    const teams: {
        id: string;
        name: string;
        role: string;
        team_role_id: number;
    }[] =
        userWithTeamMemberships?.team_memberships
            ?.map((membership) => ({
                id: membership.Team.id,
                name: membership.Team.name,
                role: membership.TeamRole?.name || 'Basic',
                team_role_id: Number(membership.team_role_id),
            }))
            // Filter out empty teams (empty name represents company association, not a real team)
            .filter((team: { name: string }) => team.name && team.name.trim() !== '') || [];

    // Get available teams for adding user to (exclude teams user is already in)
    const availableTeams =
        orgTeamsData?.teams?.filter((team) => !teams.some((userTeam) => userTeam.id === team.id)) ||
        [];

    // Team management handlers
    const handleAddToTeam = async (teamId: string) => {
        if (!userId || !orgId) return;
        try {
            await addUserToTeam.mutateAsync({
                orgId,
                teamId,
                user_id: userId,
                team_role_id: 2, // Default to Pro User role
            });
            setShowAddToTeamModal(false);
            // Refresh user data
            queryClient.invalidateQueries({ queryKey: ['adminUserDetail', orgId, userId] });
        } catch (error) {
            console.error('Failed to add user to team:', error);
            alert('Failed to add user to team');
        }
    };

    const handleRemoveFromTeam = async (teamId: string) => {
        if (!userId || !orgId) return;
        try {
            await removeUserFromTeam.mutateAsync({
                orgId,
                teamId,
                userId,
            });
            setShowRemoveFromTeamModal({ show: false });
            // Refresh user data
            queryClient.invalidateQueries({ queryKey: ['adminUserDetail', orgId, userId] });
        } catch (error) {
            console.error('Failed to remove user from team:', error);
            alert('Failed to remove user from team');
        }
    };

    const handleSetTeamRole = async (teamId: string, newRoleId: number) => {
        if (!userId || !orgId) return;
        try {
            await updateUserTeamRole.mutateAsync({
                orgId,
                teamId,
                userId,
                team_role_id: newRoleId,
            });
            // Refresh user data
            queryClient.invalidateQueries({ queryKey: ['adminUserDetail', orgId, userId] });
        } catch (error) {
            console.error('Failed to update user team role:', error);
            alert('Failed to update user team role');
        }
    };

    // Mutation for toggling admin role
    const toggleAdminRoleMutation = useMutation({
        mutationFn: async ({ isAdmin }: { isAdmin: boolean }) => {
            if (!userId || !orgId) throw new Error('User ID and Organization ID are required');
            return toggleUserAdminRole(orgId, userId, isAdmin);
        },
        onMutate: () => {
            setIsTogglingAdmin(true);
        },
        onSuccess: async (data) => {
            console.log('Admin role toggled successfully:', data.message);
            // Invalidate and refetch user data to show updated roles
            await queryClient.invalidateQueries({ queryKey: ['adminUserDetail', orgId, userId] });
            // Also invalidate any other related queries that might show this user
            await queryClient.refetchQueries({ queryKey: ['adminUserDetail', orgId, userId] });
        },
        onError: (error) => {
            console.error('Error toggling admin role:', error);
            alert(
                `Error: ${error instanceof Error ? error.message : 'Failed to toggle admin role'}`,
            );
        },
        onSettled: () => {
            setIsTogglingAdmin(false);
        },
    });

    // Wait for permissions to load before making permission decisions
    if (permissionsLoading) {
        return <LoadingPage />;
    }

    if (!hasPermission('administrate:users')) {
        return (
            <div className="bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm">
                <div
                    className="bg-[#f4f3f5] px-4 py-3 border-b border-gray-200"
                    style={{ minHeight: '42px' }}
                >
                    <div className="flex items-center gap-2">
                        <h2 className="text-xs font-bold text-[#63758e] uppercase tracking-wider">
                            Access Denied
                        </h2>
                    </div>
                </div>
                <div className="bg-white p-6">
                    <div className="text-center text-gray-600">
                        You don't have permission to view admin user details.
                    </div>
                </div>
            </div>
        );
    }

    if (userLoading) {
        return <LoadingPage />;
    }

    if (userError) {
        return (
            <div className="bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm">
                <div
                    className="bg-[#f4f3f5] px-4 py-3 border-b border-gray-200"
                    style={{ minHeight: '42px' }}
                >
                    <div className="flex items-center gap-2">
                        <h2 className="text-xs font-bold text-[#63758e] uppercase tracking-wider">
                            Error
                        </h2>
                    </div>
                </div>
                <div className="bg-white p-6">
                    <div className="text-center text-red-600">
                        Error loading user data: {(userError as Error)?.message || 'Unknown error'}
                    </div>
                </div>
            </div>
        );
    }

    if (!userId || !orgId || !userData) {
        return (
            <div className="bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm">
                <div
                    className="bg-[#f4f3f5] px-4 py-3 border-b border-gray-200"
                    style={{ minHeight: '42px' }}
                >
                    <div className="flex items-center gap-2">
                        <h2 className="text-xs font-bold text-[#63758e] uppercase tracking-wider">
                            Not Found
                        </h2>
                    </div>
                </div>
                <div className="bg-white p-6">
                    <div className="text-center text-red-600">User not found.</div>
                </div>
            </div>
        );
    }

    const user = userData.user;

    const formatDate = (dateString: string) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
            month: 'long',
            day: 'numeric',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
        });
    };

    // Helper to get user initials
    const getUserInitials = (name: string) => {
        return name
            .split(' ')
            .map((n: string) => n[0])
            .join('')
            .toUpperCase();
    };

    return (
        <div className="space-y-6">
            {/* User Profile Card */}
            <div className="bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm">
                {/* Header */}
                <div
                    className="bg-[#f4f3f5] px-4 py-3 border-b border-gray-200"
                    style={{ minHeight: '42px' }}
                >
                    <div className="flex items-center gap-2">
                        <h2 className="text-xs font-bold text-[#63758e] uppercase tracking-wider">
                            User Profile
                        </h2>
                    </div>
                </div>

                {/* User Info Content */}
                <div className="bg-white p-6">
                    <div className="flex items-center gap-4 mb-4">
                        {/* Avatar */}
                        <div className="w-16 h-16 bg-neutral-200 rounded-full flex items-center justify-center">
                            {user.picture ? (
                                <img
                                    src={user.picture}
                                    alt={user.name}
                                    className="w-16 h-16 rounded-full"
                                />
                            ) : (
                                <span className="text-xl font-medium text-neutral-600">
                                    {getUserInitials(user.name)}
                                </span>
                            )}
                        </div>

                        <div>
                            <h3 className="text-xl font-bold text-black mb-1">{user.name}</h3>
                            <p className="text-gray-600 mb-2 text-sm">{user.email}</p>
                            <div className="flex flex-wrap gap-1">
                                {user.roles && user.roles.length > 0 ? (
                                    [...user.roles]
                                        .sort()
                                        .map((role: string) => <RoleBadge key={role} role={role} />)
                                ) : (
                                    <span className="text-sm text-gray-500">No roles assigned</span>
                                )}
                            </div>

                            {/* Organization Admin Toggle */}
                            <div className="mt-4 pt-4 border-t border-gray-200">
                                <div className="flex items-center justify-between gap-4">
                                    <div className="flex-1">
                                        <label className="text-xs font-bold text-[#63758e] uppercase tracking-wider">
                                            Organization Administrator
                                        </label>
                                        <p className="text-xs text-gray-500 mt-1">
                                            Grant or revoke organization admin privileges
                                        </p>
                                    </div>
                                    <div className="flex items-center ml-4">
                                        <button
                                            onClick={() => {
                                                const currentIsAdmin =
                                                    user.roles?.includes('organization_admin') ||
                                                    false;
                                                toggleAdminRoleMutation.mutate({
                                                    isAdmin: !currentIsAdmin,
                                                });
                                            }}
                                            disabled={isTogglingAdmin}
                                            className={`inline-flex items-center px-3 py-1.5 border text-xs font-medium rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors ${
                                                user.roles?.includes('organization_admin')
                                                    ? 'border-blue-300 text-blue-600 hover:text-blue-700 hover:border-blue-400 bg-blue-50'
                                                    : 'border-gray-200 text-gray-600 hover:text-blue-600 hover:border-blue-300 bg-white'
                                            }`}
                                            title={
                                                isTogglingAdmin
                                                    ? 'Updating...'
                                                    : user.roles?.includes('organization_admin')
                                                      ? 'Remove admin privileges'
                                                      : 'Grant admin privileges'
                                            }
                                        >
                                            {isTogglingAdmin ? (
                                                <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin mr-1" />
                                            ) : user.roles?.includes('organization_admin') ? (
                                                <svg
                                                    className="w-4 h-4 mr-1"
                                                    fill="currentColor"
                                                    viewBox="0 0 20 20"
                                                >
                                                    <path
                                                        fillRule="evenodd"
                                                        d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z"
                                                        clipRule="evenodd"
                                                    />
                                                </svg>
                                            ) : (
                                                <svg
                                                    className="w-4 h-4 mr-1"
                                                    fill="none"
                                                    stroke="currentColor"
                                                    viewBox="0 0 24 24"
                                                >
                                                    <path
                                                        strokeLinecap="round"
                                                        strokeLinejoin="round"
                                                        strokeWidth={2}
                                                        d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                                                    />
                                                </svg>
                                            )}
                                            {user.roles?.includes('organization_admin')
                                                ? 'Remove Admin'
                                                : 'Make Admin'}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Information Cards */}
            <div className="grid md:grid-cols-2 gap-6">
                {/* Basic Information Card */}
                <div className="bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm">
                    {/* Header */}
                    <div
                        className="bg-[#f4f3f5] px-4 py-3 border-b border-gray-200"
                        style={{ minHeight: '42px' }}
                    >
                        <div className="flex items-center gap-2">
                            <h3 className="text-xs font-bold text-[#63758e] uppercase tracking-wider">
                                Basic Information
                            </h3>
                        </div>
                    </div>

                    {/* Content */}
                    <div className="bg-white p-4 space-y-4">
                        <div>
                            <label className="text-xs font-bold text-[#63758e] uppercase tracking-wider">
                                User ID
                            </label>
                            <p className="text-sm text-black font-mono mt-1">{user.user_id}</p>
                        </div>

                        <div>
                            <label className="text-xs font-bold text-[#63758e] uppercase tracking-wider">
                                Organization ID
                            </label>
                            <p className="text-sm text-black font-mono mt-1">
                                {user.organization_id}
                            </p>
                        </div>

                        <div>
                            <label className="text-xs font-bold text-[#63758e] uppercase tracking-wider">
                                Email
                            </label>
                            <p className="text-sm text-gray-600 mt-1">{user.email}</p>
                        </div>

                        <div>
                            <label className="text-xs font-bold text-[#63758e] uppercase tracking-wider">
                                Full Name
                            </label>
                            <p className="text-sm text-black font-bold mt-1">
                                {user.given_name && user.family_name
                                    ? `${user.given_name} ${user.family_name}`
                                    : user.name}
                            </p>
                        </div>

                        {user.nickname && (
                            <div>
                                <label className="text-xs font-bold text-[#63758e] uppercase tracking-wider">
                                    Nickname
                                </label>
                                <p className="text-sm text-gray-600 mt-1">{user.nickname}</p>
                            </div>
                        )}
                    </div>
                </div>

                {/* Authentication & Access Card */}
                <div className="bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm">
                    {/* Header */}
                    <div
                        className="bg-[#f4f3f5] px-4 py-3 border-b border-gray-200"
                        style={{ minHeight: '42px' }}
                    >
                        <div className="flex items-center gap-2">
                            <h3 className="text-xs font-bold text-[#63758e] uppercase tracking-wider">
                                Authentication & Access
                            </h3>
                        </div>
                    </div>

                    {/* Content */}
                    <div className="bg-white p-4 space-y-4">
                        <div>
                            <label className="text-xs font-bold text-[#63758e] uppercase tracking-wider">
                                Email Verified
                            </label>
                            <p
                                className={`text-sm font-bold mt-1 ${user.email_verified ? 'text-green-600' : 'text-red-600'}`}
                            >
                                {user.email_verified ? 'Verified' : 'Not Verified'}
                            </p>
                        </div>

                        <div>
                            <label className="text-xs font-bold text-[#63758e] uppercase tracking-wider">
                                Login Count
                            </label>
                            <p className="text-sm text-gray-600 mt-1">{user.login_count}</p>
                        </div>

                        <div>
                            <label className="text-xs font-bold text-[#63758e] uppercase tracking-wider">
                                Account Created
                            </label>
                            <p className="text-sm text-gray-600 mt-1">
                                {formatDate(user.created_at)}
                            </p>
                        </div>

                        <div>
                            <label className="text-xs font-bold text-[#63758e] uppercase tracking-wider">
                                Last Updated
                            </label>
                            <p className="text-sm text-black font-bold mt-1">
                                {formatDate(user.updated_at)}
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            {/* User Metadata */}
            {isDeveloper &&
                (Object.keys(user.user_metadata).length > 0 ||
                    Object.keys(user.app_metadata).length > 0) && (
                    <div className="bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm">
                        {/* Header */}
                        <div
                            className="bg-[#f4f3f5] px-4 py-3 border-b border-gray-200"
                            style={{ minHeight: '42px' }}
                        >
                            <div className="flex items-center gap-2">
                                <h3 className="text-xs font-bold text-[#63758e] uppercase tracking-wider">
                                    Metadata
                                </h3>
                            </div>
                        </div>

                        {/* Content */}
                        <div className="bg-white p-4">
                            <div className="grid md:grid-cols-2 gap-6">
                                {Object.keys(user.user_metadata).length > 0 && (
                                    <div>
                                        <h4 className="text-xs font-bold text-[#63758e] uppercase tracking-wider mb-3">
                                            User Metadata
                                        </h4>
                                        <div className="bg-gray-50 rounded-lg p-4">
                                            <pre className="text-xs text-gray-700 whitespace-pre-wrap">
                                                {JSON.stringify(user.user_metadata, null, 2)}
                                            </pre>
                                        </div>
                                    </div>
                                )}

                                {Object.keys(user.app_metadata).length > 0 && (
                                    <div>
                                        <h4 className="text-xs font-bold text-[#63758e] uppercase tracking-wider mb-3">
                                            App Metadata
                                        </h4>
                                        <div className="bg-gray-50 rounded-lg p-4">
                                            <pre className="text-xs text-gray-700 whitespace-pre-wrap">
                                                {JSON.stringify(user.app_metadata, null, 2)}
                                            </pre>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                )}

            {/* Identity Providers */}
            {isDeveloper && user.identities && user.identities.length > 0 && (
                <div className="bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm">
                    {/* Header */}
                    <div
                        className="bg-[#f4f3f5] px-4 py-3 border-b border-gray-200"
                        style={{ minHeight: '42px' }}
                    >
                        <div className="flex items-center gap-2">
                            <h3 className="text-xs font-bold text-[#63758e] uppercase tracking-wider">
                                Identity Providers
                            </h3>
                        </div>
                    </div>

                    {/* Content */}
                    <div className="bg-white p-4">
                        <div className="space-y-2">
                            {user.identities.map(
                                (identity: Record<string, unknown>, index: number) => (
                                    <div
                                        key={index}
                                        className="flex justify-between items-center text-sm py-2 px-3 bg-gray-50 rounded-md"
                                    >
                                        <span className="font-bold text-black">
                                            {(identity.provider as string) || 'Unknown'}
                                        </span>
                                        <span className="text-gray-600">
                                            Connected: {(identity.connection as string) || 'N/A'}
                                        </span>
                                    </div>
                                ),
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* Groups Card */}
            <div className="bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm">
                {/* Header */}
                <div
                    className="bg-[#f4f3f5] px-4 py-3 border-b border-gray-200"
                    style={{ minHeight: '42px' }}
                >
                    <div className="flex items-center gap-2">
                        <h3 className="text-xs font-bold text-[#63758e] uppercase tracking-wider">
                            Team Memberships
                        </h3>
                        <span className="text-xs text-[#63758e] font-medium">({teams.length})</span>
                        {hasPermission('manage:team_members') && (
                            <button
                                onClick={() => setShowAddToTeamModal(true)}
                                className="ml-auto inline-flex items-center px-2 py-1 border border-green-300 text-xs font-medium rounded-md text-green-600 bg-green-50 hover:text-green-700 hover:border-green-400 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 transition-colors"
                                title="Add to Team"
                            >
                                <svg
                                    className="w-4 h-4 mr-1"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M12 4v16m8-8H4"
                                    />
                                </svg>
                                Add to Team
                            </button>
                        )}
                    </div>
                </div>

                {/* Content */}
                <div className="bg-white p-4">
                    {!userLoading &&
                        !userError &&
                        (() => {
                            return teams.length === 0 ? (
                                <div className="text-center text-gray-500 py-4">
                                    User is not a member of any teams.
                                </div>
                            ) : (
                                <div className="space-y-3">
                                    {teams.map(
                                        (team: {
                                            id: string;
                                            name: string;
                                            role: string;
                                            team_role_id: number;
                                        }) => (
                                            <div
                                                key={team.id}
                                                className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 transition-colors"
                                            >
                                                <div className="flex items-start justify-between">
                                                    <div className="flex-1">
                                                        <div className="flex items-center gap-2 mb-1">
                                                            <h4 className="text-sm font-bold text-black">
                                                                {team.name}
                                                            </h4>
                                                            {team.team_role_id === 1 && (
                                                                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                                                                    🔒 Restricted
                                                                </span>
                                                            )}
                                                            {team.team_role_id === 2 && (
                                                                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                                                    👤 User
                                                                </span>
                                                            )}
                                                            {team.team_role_id === 3 && (
                                                                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                                                                    ⭐ Approver
                                                                </span>
                                                            )}
                                                        </div>
                                                        <div className="text-xs text-gray-500">
                                                            <span className="font-medium">
                                                                Role:
                                                            </span>{' '}
                                                            {team.role} (ID: {team.team_role_id})
                                                        </div>
                                                    </div>
                                                    <div className="flex items-center gap-1">
                                                        {/* Pro Restricted Button */}
                                                        <button
                                                            onClick={() =>
                                                                handleSetTeamRole(team.id, 1)
                                                            }
                                                            className={`inline-flex items-center px-2 py-1 border text-xs font-medium rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors ${
                                                                Number(team.team_role_id) === 1
                                                                    ? 'border-orange-400 text-orange-800 bg-orange-100'
                                                                    : 'border-orange-200 text-orange-600 hover:text-orange-700 hover:border-orange-300 bg-orange-50'
                                                            }`}
                                                            title="Set as Pro Restricted"
                                                        >
                                                            Restricted
                                                        </button>

                                                        {/* Pro User Button */}
                                                        <button
                                                            onClick={() =>
                                                                handleSetTeamRole(team.id, 2)
                                                            }
                                                            className={`inline-flex items-center px-2 py-1 border text-xs font-medium rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors ${
                                                                Number(team.team_role_id) === 2
                                                                    ? 'border-blue-400 text-blue-800 bg-blue-100'
                                                                    : 'border-blue-200 text-blue-600 hover:text-blue-700 hover:border-blue-300 bg-blue-50'
                                                            }`}
                                                            title="Set as Pro User"
                                                        >
                                                            User
                                                        </button>

                                                        {/* Pro Approver Button */}
                                                        <button
                                                            onClick={() =>
                                                                handleSetTeamRole(team.id, 3)
                                                            }
                                                            className={`inline-flex items-center px-2 py-1 border text-xs font-medium rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors ${
                                                                Number(team.team_role_id) === 3
                                                                    ? 'border-purple-400 text-purple-800 bg-purple-100'
                                                                    : 'border-purple-200 text-purple-600 hover:text-purple-700 hover:border-purple-300 bg-purple-50'
                                                            }`}
                                                            title="Set as Pro Approver"
                                                        >
                                                            Approver
                                                        </button>
                                                        <button
                                                            onClick={() =>
                                                                setShowRemoveFromTeamModal({
                                                                    show: true,
                                                                    teamId: team.id,
                                                                    teamName: team.name,
                                                                })
                                                            }
                                                            disabled={teams.length <= 1}
                                                            className={`inline-flex items-center px-2 py-1 border text-xs font-medium rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors ${
                                                                teams.length <= 1
                                                                    ? 'border-gray-200 text-gray-400 bg-gray-50'
                                                                    : 'border-red-300 text-red-600 bg-red-50 hover:text-red-700 hover:border-red-400'
                                                            }`}
                                                            title={
                                                                teams.length <= 1
                                                                    ? 'Cannot remove user from their only team'
                                                                    : 'Remove from Team'
                                                            }
                                                        >
                                                            <svg
                                                                className="w-4 h-4 mr-1"
                                                                fill="currentColor"
                                                                viewBox="0 0 20 20"
                                                            >
                                                                <path
                                                                    fillRule="evenodd"
                                                                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm-3.707-3.293a1 1 0 00-1.414 1.414L9 14.414l1.293 1.293a1 1 0 001.414-1.414l-2-2a1 1 0 00-1.414 0l-2 2z"
                                                                    clipRule="evenodd"
                                                                />
                                                            </svg>
                                                            Remove
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                        ),
                                    )}
                                </div>
                            );
                        })()}
                </div>
            </div>

            {/* Add to Team Modal */}
            {showAddToTeamModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white p-6 rounded-lg shadow-xl max-w-md w-full">
                        <h3 className="text-lg font-bold text-black mb-4">Add User to Team</h3>

                        <p className="text-sm text-gray-600 mb-4">
                            Select a team to add this user to.
                        </p>
                        <div className="space-y-3 max-h-60 overflow-y-auto">
                            {availableTeams.map((team) => (
                                <button
                                    key={team.id}
                                    onClick={() => handleAddToTeam(team.id)}
                                    className="w-full inline-flex items-center justify-between px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                                >
                                    {team.name}
                                    <svg
                                        className="w-4 h-4"
                                        fill="none"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24"
                                    >
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M9 5l7 7-7 7"
                                        />
                                    </svg>
                                </button>
                            ))}
                        </div>
                        <div className="mt-4 flex justify-end">
                            <button
                                onClick={() => setShowAddToTeamModal(false)}
                                className="px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Remove from Team Modal */}
            {showRemoveFromTeamModal.show && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white p-6 rounded-lg shadow-xl max-w-md w-full">
                        <h3 className="text-lg font-bold text-black mb-4">Remove User from Team</h3>
                        <p className="text-sm text-gray-600 mb-4">
                            Are you sure you want to remove{' '}
                            <span className="font-semibold">
                                {showRemoveFromTeamModal.teamName}
                            </span>{' '}
                            from the team?
                        </p>
                        <div className="flex justify-end space-x-2">
                            <button
                                onClick={() => setShowRemoveFromTeamModal({ show: false })}
                                className="px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={() =>
                                    handleRemoveFromTeam(showRemoveFromTeamModal.teamId!)
                                }
                                className="px-4 py-2 border border-red-300 text-sm font-medium rounded-md text-red-600 bg-red-50 hover:bg-red-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                            >
                                Remove
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
